import React from "react";
import { Card, InfoGroup, InfoRow, SubHeadline, Tooltip } from "@KristianIV/common-ui";
import { formatMinutes } from "../../util/duration";
import { TitanStatus } from "../model/scout";
import { WallieStats, droppedFramesText } from "../wallie";
import styles from "../OrganisationPage.module.css";

const isSet = (value: string | null | undefined): value is string => typeof value === "string" && value !== "";

const Explained = ({ text, children }: { text: string; children: React.ReactNode }) => (
  <Tooltip content={text}>
    <span>{children}</span>
  </Tooltip>
);

/** The Wallie board attached to a scout: what it runs, how long it has been up, when it last reported, and its dropped frames and temperature when the scout's diagnostics carry them. */
const WallieCard = ({ titan, stats }: { titan: TitanStatus; stats?: WallieStats }) => (
  <Card interactive={false} padded>
    <SubHeadline>{titan.type || "Wallie"}</SubHeadline>
    <InfoGroup>
      {isSet(titan.serialNumber) && (
        <InfoRow label="Serial number">
          <span className={styles.mono}>{titan.serialNumber}</span>
        </InfoRow>
      )}
      <InfoRow label="Firmware">
        <Explained text="Identifier of the firmware the board runs.">
          <span className={styles.mono}>{titan.firmware}</span>
        </Explained>
      </InfoRow>
      {isSet(titan.version) && <InfoRow label="Version">{titan.version}</InfoRow>}
      {isSet(titan.configHash) && (
        <InfoRow label="Config hash">
          <Explained text="Hash of the configuration the board runs; changes when it picks up new settings.">
            <span className={styles.mono}>{titan.configHash}</span>
          </Explained>
        </InfoRow>
      )}
      <InfoRow label="Uptime">
        <Explained text="Time since the board booted.">{formatMinutes(titan.uptimeMin)}</Explained>
      </InfoRow>
      <InfoRow label="Last update">
        <Explained text="Seconds since the board last reported to the scout; 0 means it reported in the same status cycle.">
          {titan.lastUpdateSec} s ago
        </Explained>
      </InfoRow>
      {stats && (
        <InfoRow label="Drop">
          <Explained text="Audio frames the board dropped in the last minute, hour and 24 hours. Anything above 0 means audio was lost.">
            {droppedFramesText(stats)}
          </Explained>
        </InfoRow>
      )}
      {typeof stats?.temperature === "number" && (
        <InfoRow label="Temperature">
          <Explained text="Board temperature.">{stats.temperature.toFixed(1)} °C</Explained>
        </InfoRow>
      )}
    </InfoGroup>
  </Card>
);

export default WallieCard;
